import { motion } from "framer-motion";
import { Headphones, MessageSquare, ShoppingCart, LifeBuoy, ArrowRight, Cpu } from "lucide-react";

/**
 * Home "What we do", a calm explainer diagram (NOT a heavy 3D render).
 *
 *  ┌────────┐   ┌─────────────┐   ┌──────────────┐
 *  │ Inputs │ → │ Kyro engine │ → │ Outcomes     │
 *  └────────┘   └─────────────┘   └──────────────┘
 *      use-cases       core           channels & systems
 */

const INPUTS = [
    "A customer flow",
    "Your knowledge",
    "Your systems",
    "Languages you need",
];

const SKILLS = [
    { icon: Headphones, label: "CSAT" },
    { icon: MessageSquare, label: "Booking" },
    { icon: ShoppingCart, label: "Outbound" },
    { icon: LifeBuoy, label: "Support" },
];

const OUTPUTS = [
    "Voice",
    "Chat",
    "WhatsApp",
    "Email",
    "CRM",
    "Ticketing",
];

export default function HomeFlow() {
    return (
        <motion.div
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6 }}
            style={{
                border: "1px solid var(--line)",
                borderRadius: 18,
                background:
                    "radial-gradient(60% 60% at 50% 0%, rgba(143,208,255,0.10), transparent 60%)," +
                    "linear-gradient(180deg, #10131a, #0a0c12)",
                padding: 28,
                position: "relative",
                overflow: "hidden",
            }}
        >
            <div
                style={{
                    display: "grid",
                    gridTemplateColumns: "1fr auto 1fr auto 1fr",
                    alignItems: "stretch",
                    gap: 18,
                }}
                className="flow-grid"
            >
                <Col label="01 · Tell us">
                    <ul style={ulStyle}>
                        {INPUTS.map((i, idx) => (
                            <li key={idx} style={liStyle}>
                                <Dot />
                                {i}
                            </li>
                        ))}
                    </ul>
                </Col>

                <Arrow />

                <Col label="02 · We build the agent" highlight>
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 10,
                            marginBottom: 14,
                            color: "var(--accent)",
                        }}
                    >
                        <Cpu size={16} />
                        <span
                            className="font-display"
                            style={{ fontWeight: 600, fontSize: ".95rem", color: "#fff" }}
                        >
                            Kyro Orchestrator
                        </span>
                    </div>
                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gap: 8,
                        }}
                    >
                        {SKILLS.map((s, i) => {
                            const Icon = s.icon;
                            return (
                                <div
                                    key={s.label}
                                    style={{
                                        border: "1px solid rgba(143,208,255,0.22)",
                                        background: "rgba(143,208,255,0.06)",
                                        borderRadius: 10,
                                        padding: "10px 12px",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 8,
                                        fontSize: ".82rem",
                                    }}
                                >
                                    <Icon size={13} style={{ color: "var(--accent)" }} />
                                    {s.label}
                                </div>
                            );
                        })}
                    </div>
                </Col>

                <Arrow />

                <Col label="03 · It runs across">
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                        {OUTPUTS.map((o) => (
                            <span
                                key={o}
                                style={{
                                    border: "1px solid var(--line-strong)",
                                    background: "rgba(255,255,255,0.02)",
                                    borderRadius: 999,
                                    padding: "6px 12px",
                                    fontSize: ".78rem",
                                    color: "var(--ink)",
                                    fontFamily: "JetBrains Mono, monospace",
                                }}
                            >
                                {o}
                            </span>
                        ))}
                    </div>
                </Col>
            </div>

            <style>{`
                @media (max-width: 880px) {
                    .flow-grid { grid-template-columns: 1fr !important; }
                    .flow-arrow { display: none !important; }
                }
            `}</style>
        </motion.div>
    );
}

function Col({ label, highlight, children }) {
    return (
        <div
            style={{
                border: highlight ? "1px solid rgba(143,208,255,0.35)" : "1px solid var(--line)",
                background: highlight
                    ? "linear-gradient(180deg, rgba(143,208,255,0.08), rgba(143,208,255,0.02))"
                    : "rgba(255,255,255,0.025)",
                borderRadius: 14,
                padding: 18,
                minHeight: 220,
            }}
        >
            <div
                className="font-mono-label"
                style={{
                    color: highlight ? "var(--accent)" : "var(--kl-muted)",
                    marginBottom: 14,
                }}
            >
                {label}
            </div>
            {children}
        </div>
    );
}

function Arrow() {
    return (
        <div
            className="flow-arrow"
            style={{
                display: "flex",
                alignItems: "center",
                color: "var(--accent)",
                opacity: 0.8,
            }}
        >
            <ArrowRight size={20} />
        </div>
    );
}

const ulStyle = { listStyle: "none", padding: 0, margin: 0, display: "grid", gap: 9 };
const liStyle = {
    display: "flex",
    alignItems: "center",
    gap: 10,
    fontSize: ".88rem",
    color: "var(--ink)",
};

function Dot() {
    return (
        <span
            aria-hidden
            style={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                background: "var(--accent)",
                boxShadow: "0 0 8px rgba(143,208,255,0.5)",
                flexShrink: 0,
            }}
        />
    );
}
